'use client'

import { useCaptionOverflow } from '@/utils/react-hooks/useCaptionOverflow'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface IProps {
  imageCaption?: string
  showCaptions: boolean
  hideCaptions: () => void
}

export const GalleryCaption = ({ imageCaption, showCaptions, hideCaptions }: IProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    setIsExpanded(false)
  }, [imageCaption])

  const { ref: captionRef, isOverflowing } = useCaptionOverflow({
    enabled: showCaptions && !!imageCaption,
    text: imageCaption,
    isExpanded,
  })

  const onHideCaptions = () => {
    setIsExpanded(false)
    hideCaptions()
  }

  const canToggle = isOverflowing || isExpanded

  return (
    <figcaption
      className={`absolute z-[100] flex justify-between rounded-b-lg bottom-[0.01px] left-0 w-full italic bg-black bg-opacity-45 text-gray-200 text-sm py-2 px-2 sm:py-4 sm:px-4 transition-all duration-500 ease-out ${showCaptions ? '' : 'opacity-0 invisible'
        }`}
    >
      <div
        ref={captionRef}
        className={`text-xs sm:text-sm min-w-0 flex-1 break-words ${isExpanded ? 'line-clamp-none' : 'line-clamp-3'
          }`}
      >
        {imageCaption}
      </div>

      <div className="ml-2 flex shrink-0 items-center gap-1">
        <button
          type="button"
          className={`rounded p-1 transition ${canToggle ? 'text-gray-200/90 hover:text-gray-100 hover:bg-white/10' : 'opacity-0 pointer-events-none'
            }`}
          onClick={() => setIsExpanded((v) => !v)}
          aria-label={isExpanded ? 'Collapse caption' : 'Expand caption'}
          title={isExpanded ? 'Collapse caption' : 'Expand caption'}
        >
          <span aria-hidden="true" className="text-lg leading-none">
            ⋯
          </span>
        </button>

        <button
          type="button"
          className="rounded p-1 hover:bg-white/10 transition"
          onClick={onHideCaptions}
          aria-label="Close caption"
          title="Close caption"
        >
          <Image src="/images/favicons/close-flowbite.svg" alt="Close caption" width={24} height={24} />
        </button>
      </div>
    </figcaption>
  )
}

export default GalleryCaption
